/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'

interface MarkdownProps {
  children: string
  className?: string
  renderHtmlInSandbox?: boolean
}

const PURE_HTML_START =
  /^<(?:!doctype\s+html|html|head|body|main|section|article|aside|header|footer|div|p|table|ul|ol|h[1-6]|figure|details|dialog|form|style|svg)\b/i

export function isPureHtmlContent(content: string): boolean {
  const normalized = content
    .trim()
    .replace(/^(?:<!--[\s\S]*?-->\s*)+/, '')
  return PURE_HTML_START.test(normalized)
}

function sanitizeSandboxFragment(content: string): string {
  return content
    .replace(/<!doctype[^>]*>/gi, '')
    .replace(/<\/?(?:html|head|body)\b[^>]*>/gi, '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, '')
    .replace(/<script\b[^>]*\/?\s*>/gi, '')
    .replace(/<(?:meta|base|link)\b[^>]*\/?\s*>/gi, '')
}

export function buildSandboxedHtmlDocument(
  content: string,
  frameId: string
): string {
  const fragment = sanitizeSandboxFragment(content)
  const serializedFrameId = JSON.stringify(frameId)

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: http: data: blob:; media-src https: http: data: blob:; font-src https: data:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; connect-src 'none'; frame-src 'none'; object-src 'none'; form-action 'none'">
  <style>
    html,body{margin:0;min-height:100%;background:transparent;color:inherit;overflow:auto}
    *,*::before,*::after{box-sizing:border-box}
    button,a,[role="button"],[onclick]{transition:filter .12s ease,transform .12s ease,box-shadow .12s ease}
    .__announcement_clicked{filter:brightness(.92);transform:scale(.985);box-shadow:0 0 0 3px rgba(59,130,246,.22)!important}
    #__announcement_feedback{position:fixed;left:50%;bottom:18px;z-index:2147483647;transform:translate(-50%,12px);padding:7px 12px;border-radius:999px;background:rgba(17,24,39,.9);color:#fff;font:600 12px/1.2 system-ui,sans-serif;opacity:0;pointer-events:none;transition:opacity .16s ease,transform .16s ease}
    #__announcement_feedback.__show{opacity:1;transform:translate(-50%,0)}
  </style>
</head>
<body>
${fragment}
<div id="__announcement_feedback" role="status" aria-live="polite">✓ 操作已响应</div>
<script>
(() => {
  const frameId = ${serializedFrameId};
  const feedback = document.getElementById('__announcement_feedback');
  let feedbackTimer;
  const postHeight = () => {
    const height = Math.max(
      document.documentElement.scrollHeight || 0,
      document.body.scrollHeight || 0,
      260
    );
    parent.postMessage({ type: 'new-api:announcement-html-resize', frameId, height }, '*');
  };
  const showFeedback = () => {
    if (!feedback) return;
    feedback.classList.add('__show');
    clearTimeout(feedbackTimer);
    feedbackTimer = setTimeout(() => feedback.classList.remove('__show'), 900);
  };
  document.querySelectorAll('a[href]').forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });
  document.addEventListener('click', (event) => {
    const element = event.target instanceof Element
      ? event.target.closest('button,a,[role="button"],[onclick]')
      : null;
    if (!element) return;
    element.classList.add('__announcement_clicked');
    setTimeout(() => element.classList.remove('__announcement_clicked'), 180);
    showFeedback();
    setTimeout(postHeight, 0);
  });
  addEventListener('load', postHeight);
  new ResizeObserver(postHeight).observe(document.documentElement);
  postHeight();
})();
</script>
</body>
</html>`
}

function SandboxedHtml({
  content,
  className,
}: {
  content: string
  className?: string
}) {
  const reactId = useId()
  const frameId = `announcement-${reactId}`
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(360)
  const srcDoc = useMemo(
    () => buildSandboxedHtmlDocument(content, frameId),
    [content, frameId]
  )

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return
      const data = event.data as {
        type?: string
        frameId?: string
        height?: number
      }
      if (
        data.type !== 'new-api:announcement-html-resize' ||
        data.frameId !== frameId ||
        typeof data.height !== 'number'
      ) {
        return
      }
      const viewportLimit = Math.min(window.innerHeight * 0.65, 720)
      setHeight(Math.min(Math.max(data.height, 260), viewportLimit))
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [frameId])

  return (
    <iframe
      ref={frameRef}
      title='Interactive announcement'
      className={cn('w-full rounded-md border-0 bg-transparent', className)}
      style={{ height }}
      sandbox='allow-scripts allow-popups'
      referrerPolicy='no-referrer'
      srcDoc={srcDoc}
    />
  )
}

export function Markdown({
  children,
  className,
  renderHtmlInSandbox = false,
}: MarkdownProps) {
  if (renderHtmlInSandbox && isPureHtmlContent(children)) {
    return <SandboxedHtml content={children} className={className} />
  }

  return (
    <div
      className={cn(
        'prose prose-sm dark:prose-invert max-w-none',
        'prose-headings:font-semibold prose-headings:tracking-tight',
        'prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg',
        'prose-p:leading-relaxed prose-p:my-2',
        'prose-a:text-primary prose-a:no-underline hover:prose-a:underline',
        'prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none',
        'prose-pre:bg-muted prose-pre:border',
        'prose-blockquote:border-l-primary prose-blockquote:bg-muted/50 prose-blockquote:py-1',
        'prose-ul:my-2 prose-ol:my-2 prose-li:my-1',
        'prose-table:border prose-thead:bg-muted',
        'prose-td:border prose-th:border prose-td:px-3 prose-th:px-3',
        'prose-img:rounded-lg prose-img:shadow-sm',
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        '[overflow-wrap:anywhere] break-words',
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // 自定义组件渲染（可选）
          a: ({ node, ...props }) => (
            <a {...props} target='_blank' rel='noopener noreferrer' />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}
